import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mdiCheck, mdiClose, mdiFileDocumentMultiple } from '@mdi/js';
import { TableTitle } from 'common/components/tables';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import {
  FilterProp,
  FiltersContainer,
  FilterTypes,
  OptionsFilterComponentHooks,
  useFiltration,
} from 'common/components/table/filtering';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import {
  MollyWhitelabelCustomerKYCDocument,
  MollyWhitelabelCustomerKYCDocumentStatuses,
} from 'common/state/molly-whitelabel/actions';
import { mollyWhitelabelKYCDocumentsRecipe } from 'common/state/molly-whitelabel/recipes';
import { defaultMollyWhitelabelKYCDocumentsState } from 'common/state/molly-whitelabel/states';
import {
  MollyWhitelabelKYCDocumentsParams,
  doGetMollyWhitelabelKYCDocuments,
} from 'common/state/molly-whitelabel/thunkActions';
import Icon from '@mdi/react';
import styled, { useTheme } from '@mollybet/ui/dist/styled';
import { Link } from 'react-router-dom';
import { Button } from '@mollybet/ui';

export const UsernameLink = styled(Link)`
  width: fit-content;
  padding-bottom: 0.125rem;
  border-bottom: 1px dashed;
  cursor: pointer;
  color: inherit;
`;

export const DocumentsTable = () => {
  const theme = useTheme();
  const dropdownRef = useRef();
  const [state, dispatch] = useProducerWithThunks(
    mollyWhitelabelKYCDocumentsRecipe,
    defaultMollyWhitelabelKYCDocumentsState
  );
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const updateTimeout = useRef<ReturnType<typeof setTimeout>>();

  const columns = useMemo(
    (): ColumnProp<MollyWhitelabelCustomerKYCDocument>[] => [
      { key: 'id', title: 'Id' },
      { key: 'contact_username', title: 'Username' },
      { key: 'description', title: 'Description' },
      {
        key: 'issue_date',
        title: 'Issue Date',
        valueFormatter: (row) => new Date(row.issue_date).toLocaleDateString(),
      },
      {
        key: 'expiry_date',
        title: 'Expiry Date',
        valueFormatter: (row) => new Date(row.expiry_date).toLocaleDateString(),
      },
      {
        key: 'proof_of_identity',
        title: 'Proof of Identity',
        valueFormatter: (row) => (
          <Icon
            path={row.proof_of_identity ? mdiCheck : mdiClose}
            color={row.proof_of_identity ? theme.primary : theme.danger}
            size={0.75}
          />
        ),
      },
      {
        key: 'proof_of_address',
        title: 'Proof of Address',
        valueFormatter: (row) => (
          <Icon
            path={row.proof_of_address ? mdiCheck : mdiClose}
            color={row.proof_of_address ? theme.primary : theme.danger}
            size={0.75}
          />
        ),
      },
      { key: 'status', title: 'Status' },
      {
        key: 'admin',
        title: 'Actions',
        valueFormatter: (row) => (
          <Button
            onClick={() =>
              (window.location.href = `${window.urls.admin}totus/customerdocument/${row.id}/change/`)
            }
          >
            Admin
          </Button>
        ),
      },
    ],
    [theme]
  );

  const filters = useMemo(
    (): FilterProp<MollyWhitelabelCustomerKYCDocument>[] => [
      {
        key: 'status',
        label: 'status',
        type: FilterTypes.OPTIONS,
        options: Object.values(MollyWhitelabelCustomerKYCDocumentStatuses).map(
          (status) => ({
            value: status,
            label: status,
          })
        ),
        inputProps: {
          menuPortalTarget: dropdownRef.current,
        },
      },
    ],
    [dropdownRef]
  );

  const [rows, filterComponents, filterHooks] = useFiltration(
    state.documents || [],
    filters
  );

  useEffect(() => {
    const filters: MollyWhitelabelKYCDocumentsParams = {
      limit: pageSize,
      offset: pageSize * (pageNo - 1),
    };

    filterHooks.status =
      filterHooks?.status as OptionsFilterComponentHooks<MollyWhitelabelCustomerKYCDocument>;

    if (filterHooks.status?.value?.value)
      filters.status = filterHooks.status.value
        .value as MollyWhitelabelCustomerKYCDocumentStatuses;

    clearTimeout(updateTimeout.current);
    updateTimeout.current = setTimeout(
      () => dispatch(doGetMollyWhitelabelKYCDocuments(filters)),
      300
    );
  }, [dispatch, filterHooks, pageSize, pageNo]);

  return (
    <>
      <TableTitle icon={mdiFileDocumentMultiple} title="KYC Documents" />
      <FiltersContainer hooks={filterHooks}>
        {filterComponents.status}
      </FiltersContainer>
      <div ref={dropdownRef} />
      <br />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName="KYC Documents"
        pagination={{
          count: state.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isLoading={state.pending}
        error={state.error}
        isSortable={false}
      />
    </>
  );
};
